import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Header as VoltHeader,
  Users as UsersIcon,
  Notification as NotificationIcon,
  Notifications,
  CustomerSelector,
} from "@volt_developers/react-ui-components";
import { useHistory, useLocation, matchPath } from "react-router";
import { useTranslation } from "react-i18next";
import { logout } from "../store/authSlice";
import { selectCurrentUser } from "../store/usersSlice";
import { useOperationState } from "../hooks/operationsState";
import { selectClientsWithFailingNotifications } from "../store/notificationsSlice";
import { Unarray } from "../utils/types";
import appRoutes from "../routes/index";
import { useMenu } from "../hooks/menu";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/Ability.context";
import {
  clearImpersonatedCustomer,
  selectImpersonatedCustomer,
} from "../store/appSlice";

type NotificationType = Unarray<Notifications>;

function Header() {
  const { t } = useTranslation("common");
  const ability = useAbility(AbilityContext);
  const { menu } = useMenu(appRoutes, ability);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const clientNotifications = useSelector(
    selectClientsWithFailingNotifications
  );
  const user = useSelector(selectCurrentUser) || {};
  const { operationLoading } = useOperationState("users.current", {
    defaultLoading: true,
  });
  const impersonatedCustomer = useSelector(selectImpersonatedCustomer);

  const handleCustomerClear = useCallback(() => {
    dispatch(clearImpersonatedCustomer());
  }, [dispatch]);

  let letters: string | null = useMemo(() => {
    if (typeof user?.name !== "string") {
      return null;
    }
    let letters = user.name.replace(/[^A-Z]+/g, "").substring(0, 2);
    if (letters === "") {
      letters = user.name.substring(0, 2).toUpperCase();
    }
    return letters;
  }, [user?.name]);

  const signOut = useCallback((): void => {
    dispatch(logout());
  }, [dispatch]);

  const notifications: Notifications = useMemo(() => {
    if (!clientNotifications || !Array.isArray(clientNotifications)) {
      return [];
    }
    return clientNotifications
      .filter((item: any) => "client" in item)
      .map(
        (item: any): NotificationType => ({
          title: "Notifications disabled",
          content: (
            <>
              Payment notifications are currently disabled for{" "}
              <strong>{item.client.name}</strong>. To fix the problem you'll
              need to send a successful response to a test to re-enable payment
              notifications.
            </>
          ),
          icon: NotificationIcon,
          action: () => {
            history.push(`/configuration/applications/${item.client.id}`);
          },
          actionLabel: t("View Details"),
          status: "error",
        })
      );
  }, [clientNotifications, history, t]);

  const title = useMemo<string | undefined>(() => {
    for (let i = 0; i < menu.length; i++) {
      let match = matchPath(location.pathname, {
        path: menu[i].path,
        exact: menu[i].path === "/",
      });
      if (match) {
        if (menu[i].showChildrenInSidebar && menu[i].children) {
          for (let j = 0; j < menu[i].children!.length; j++) {
            match = matchPath(location.pathname, {
              path: menu[i].children![j].path,
            });
            if (match) {
              return menu[i].children![j].i18nName;
            }
          }
        }
        return menu[i].i18nName;
      }
    }
    return undefined;
  }, [location.pathname, menu]);

  return (
    <VoltHeader
      title={title}
      customerSelector={
        impersonatedCustomer ? (
          <CustomerSelector
            current={impersonatedCustomer}
            onClear={handleCustomerClear}
          />
        ) : undefined
      }
      username={user?.name || ""}
      avatarLetters={letters || undefined}
      isLoading={operationLoading}
      notifications={notifications}
      userMenu={[{ i18nName: t("Sign out"), icon: UsersIcon, action: signOut }]}
    />
  );
}

export default Header;
